import React, {useEffect, useState} from 'react';
import ReactDOM from 'react-dom/client';
import {useNavigate} from "react-router";
import { BrowserRouter, Routes, Route } from 'react-router-dom'
import { ShowMore } from '@re-dev/react-truncate';

// Material UI
import Box from '@mui/material/Box';
import FormControl from '@mui/material/FormControl';
import Grid from '@mui/material/Unstable_Grid2';
import Button from "@mui/material/Button";
import Container from "@mui/material/Container";
import Divider from "@mui/material/Divider";
import FormControlLabel from "@mui/material/FormControlLabel";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import Chip from "@mui/material/Chip";
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';

// App imports
import './index.css';
import FacetSelector from "./FacetSelector.tsx";
import RepositoryGrid from "./RepositoryGrid";
import RepositoryHomePage from "./repo_details/RepositoryHomePage"
import RepositoryComparisonPage from "./repository_comparison/RepositoryComparisonPage";
import MainHeader from "./MainHeader";
import Footer from "./Footer";
import RepositoryAddEditPage from "./repository_management/RepositoryAddEditPage";
import RepositoryManagementHome from "./repository_management/RepositoryManagementHome";
import DataDictionaryPage from "./data_dictionary/DataDictionaryPage";
import useAuthString from "./useAuthString";
import RepositoryPreviewPage from "./repository_management/RepositoryPreviewPage";
import RepositoryAcceptedForReview from "./repository_management/RepostioryAcceptedForReview";
import Login from "./login/Login";
import RepositoryAcceptedForPublish from "./repository_management/RepositoryAcceptedForPublish";
import MaintenancePage from "./maintenance/maintenance-mode";
import PageNavigation from './PageNavigation.jsx';

const ImportantReminderBanner = ({ onClose }) => {
    return (
        <div className="reminder-banner">
            <button
                onClick={onClose}
                className="reminder-banner-close"
            >
                ✕
            </button>
            <div className="reminder-banner-content">
                <div className="reminder-banner-title">
                    Important Reminder
                </div>
                <div className="reminder-banner-text">
                    Prioritize domain-specific data repositories! These repositories offer valuable curation services and advanced
                    features that enhance the visibility, accessibility, and quality of shared data. NIH expects researchers to use
                    domain-specific data repositories over generalist repositories, when available.
                </div>
            </div>
        </div>
    );
};

const ComparisonSelection = (props) => {
    useEffect(() => {
        console.log('banner prop:' + props.maintenanceBanner);
    });

    const navigate = useNavigate();

    const handleCompare = () => {
       var searchStr = '';

       if (props.compareIds.length < 2) {
           // TODO Add popup for user
           // console.log("Compare requires 2 or more repos selected - doing nothing");
       } else {
           searchStr += '?repo1=' + props.compareIds[0];
           if (props.compareIds.length > 1) {
               searchStr += '&repo2=' + props.compareIds[1];
           }
           if (props.compareIds.length > 2) {
               searchStr += '&repo3=' + props.compareIds[2];
           }
           // console.log(searchStr);

           navigate({
               pathname: '/repositories/compare',
               search: searchStr
           })
       }
    };

    const handleRemoveFromCompare = (repoId) => {
        if (props.handleRemoveFromCompare) {
            props.handleRemoveFromCompare(repoId);
        }
    };

    return (<Box className='comparison-selection'>
        <Grid container spacing={2}>
            <Grid xs={7} sx={{ paddingTop: '20px'}}>
                <TextField id="search-bar" label="Search" variant="filled" fullWidth onChange={props.handleSearch}/>
            </Grid>

            <Grid xs={5}>
                <div style={{ float: 'right'}}>
                    <Typography><b>Choose up to 3 repositories to compare</b></Typography>

                      <div className="comparison-chips-container">
                        <Chip
                            className={"comparison-titles " + (props.compareIds[0] && "comparison-title-selected")}
                            key={'compare-1'}
                            label={props.compareIds[0] || '1'}
                            onDelete={props.compareIds[0] ? () => handleRemoveFromCompare(props.compareIds[0]) : undefined}
                        />

                        <Chip
                            className={"comparison-titles " + (props.compareIds[1] && "comparison-title-selected")}
                            key={'compare-2'}
                            label={props.compareIds[1] || '2'}
                            onDelete={props.compareIds[1] ? () => handleRemoveFromCompare(props.compareIds[1]) : undefined}
                        />

                        <Chip
                            className={"comparison-titles " + (props.compareIds[2] && "comparison-title-selected")}
                            key={'compare-3'}
                            label={props.compareIds[2] || '3'}
                            onDelete={props.compareIds[2] ? () => handleRemoveFromCompare(props.compareIds[2]) : undefined}
                        />
                    </div>


                    <Button onClick={handleCompare} id="compare_repositories_button" className="large-button" variant="contained" sx={{ boxShadow: 'none' }}>
                        Compare
                    </Button>
                </div>
            </Grid>
        </Grid>

    </Box>);
}

const SelectorInputs = (props) => {

    return ( <nav aria-label="Filters">
        <Box className='filters-wrapper'>
            <h3 className='panel-label' style={{ marginLeft: '20px' }}>Filters</h3>

            <FormControl className="filters">
                <ShowMore lines={3} trimWhitespace={true}>
                    <div className="filter-explain" >
                        <p>
                            Start typing in a box to view related terms. Terms are an exact match to repository tags; select all related terms within a filter box for a broad search.
                        </p>
                        <p>
                            <b>Broad search example</b>: To see all repositories that handle image data, type "image" in the "Data Types" field, and select all items listed.
                        </p>
                        <p>
                            <b>Narrow search example</b>: Select "Image" term to see only those repositories that use the specific term "Image" (for instance, will not return "Medical Imaging").
                        </p>
                    </div>
                </ShowMore>
                <br />
                <Divider />

                <fieldset>
                    <Tooltip arrow title="Filter by repository name">
                        <legend>Filter by <b>Name</b>. </legend>
                    </Tooltip>
                    <FacetSelector filters={props.filters} handleAddToFilters={props.handleChangeNames} facet_name="Names" facet="names" />
                </fieldset>
                <Divider />

                  <fieldset>
                    <Tooltip arrow title="Filter items match exactly to repository tags. Select all similar items for a general search">
                        <legend>Filter by <b>Repository Type</b>. Select all that apply.</legend>
                    </Tooltip>
                    <FacetSelector filters={props.filters} handleAddToFilters={props.handleChangeGeneralist} facet_name="Repository Type" facet="generalist" />
                </fieldset>
                <Divider />

                <fieldset >
                    <Tooltip arrow title="Filter items match exactly to repository tags. Select all similar items for a general search">
                        <legend>Filter by <b>Organism</b>. Select all that apply.</legend>
                    </Tooltip>
                    <FacetSelector filters={props.filters} handleAddToFilters={props.handleChangeOrganisms} facet_name="Organisms" facet="organisms" />
                </fieldset>
                <Divider />

                <fieldset>
                    <Tooltip arrow title="Filter items match exactly to repository tags. Select all similar items for a general search">
                        <legend>Filter by <b>Research Area</b>. Select all that apply.</legend>
                    </Tooltip>
                    <FacetSelector filters={props.filters} handleAddToFilters={props.handleChangeResearchAreas} facet_name="Research Areas" facet="research_areas" />
                </fieldset>
                <Divider />

                <fieldset>
                    <Tooltip arrow title="Filter items match exactly to repository tags. Select all similar items for a general search">
                        <legend>Filter by <b>Data Type</b>. Select all that apply.</legend>
                    </Tooltip>
                    <FacetSelector filters={props.filters} handleAddToFilters={props.handleChangeDataTypes} facet_name="Data Types" facet="data_types" />
                </fieldset>
                <Divider />

                <fieldset>
                    <Tooltip arrow title="Filter items match exactly to repository tags. Select all similar items for a general search">
                    <legend>Filter by <b>Data Format</b>. Select all that apply.</legend>
                    </Tooltip>
                    <FacetSelector filters={props.filters} handleAddToFilters={props.handleChangeDataFormats} facet_name="Data Formats" facet="data_formats" />
                </fieldset>
                <Divider />

                <fieldset>
                    <legend>Significant metadata required?</legend>
                    <RadioGroup
                        id="metadata_group"
                        row
                        defaultValue=""
                        onChange={props.handleAddToFilters}
                        name="metadata_group"
                    >
                        <FormControlLabel value="true" key='metadata_1' control={<Radio/>} label= 'Yes' />
                        <FormControlLabel value="false" key='metadata_2' control={<Radio/>} label= 'No' />
                        <FormControlLabel value="" key='metadata_3' control={<Radio/>} label= 'All' />
                    </RadioGroup>
                </fieldset>
                <Divider />

                <fieldset>
                    <legend>Individual-level data accepted?</legend>
                    <RadioGroup
                        id="individual_group"
                        row
                        defaultValue=""
                        onChange={props.handleAddToFilters}
                        name="individual_group"
                    >
                        <FormControlLabel value="true" key='individual_1' control={<Radio/>} label= 'Yes' />
                        <FormControlLabel value="false" key='individual_2' control={<Radio/>} label= 'No' />
                        <FormControlLabel value="" key='individual_3' control={<Radio/>} label= 'All' />
                    </RadioGroup>
                </fieldset>
                <Divider />

                <fieldset>
                    <legend>Controlled Access</legend>
                    <RadioGroup
                        id="ac_group"
                        row
                        defaultValue=""
                        onChange={props.handleAddToFilters}
                        name="ac_group"
                    >
                        <FormControlLabel value="true" key='ac_1' control={<Radio/>} label= 'Yes' />
                        <FormControlLabel value="false" key='ac_2' control={<Radio/>} label= 'No' />
                        <FormControlLabel value="" key='ac_3' control={<Radio/>} label= 'All' />
                    </RadioGroup>
                </fieldset>
                <Divider />

                <fieldset>
                    <legend>Data Submission Agreement/Institutional Certification Required for Data Submission</legend>
                    <RadioGroup
                        id="cert_group"
                        row
                        defaultValue=""
                        onChange={props.handleAddToFilters}
                        name="cert_group"
                    >
                        <FormControlLabel value="true" key='cert_1' control={<Radio/>} label= 'Yes' />
                        <FormControlLabel value="false" key='cert_2' control={<Radio/>} label= 'No' />
                        <FormControlLabel value="" key='cert_3' control={<Radio/>} label= 'All' />
                    </RadioGroup>
                </fieldset>
                <Divider />

                <fieldset>
                    <legend>Data Volume Limit</legend>
                    <RadioGroup
                        id="data_volume_group"
                        row
                        defaultValue=""
                        onChange={props.handleAddToFilters}
                        name="data_volume_group"
                    >
                        <FormControlLabel value="true" key='volume_1' control={<Radio/>} label= 'Yes' />
                        <FormControlLabel value="false" key='volume_2' control={<Radio/>} label= 'No' />
                        <FormControlLabel value="" key='volume_3' control={<Radio/>} label= 'All' />
                    </RadioGroup>
                </fieldset>
                <Divider />

                <fieldset>
                    <legend>Is there a fee to submit data?</legend>
                    <RadioGroup
                        id="fee_group"
                        row
                        defaultValue=""
                        onChange={props.handleAddToFilters}
                        name="fee_group"
                    >
                        <FormControlLabel value="true" key='fee_1' control={<Radio/>} label= 'Yes' />
                        <FormControlLabel value="false" key='fee_2' control={<Radio/>} label= 'No' />
                        <FormControlLabel value="" key='fee_3' control={<Radio/>} label= 'All' />
                    </RadioGroup>
                </fieldset>
            </FormControl>
        </Box>
    </nav>) ;
}


const RecommendedRepositories = ({ compareIds, searchResultsLoaded, searchResults, handleAddToCompare, handleRemoveFromCompare, showReminderBanner, handleCloseBanner, ...props }) => {

    return( <div>
        <Box className='repository-list'>
            {/* Banner appears here, above the search results */}
            {showReminderBanner && (
                <Box sx={{ marginBottom: '16px', marginLeft: '20px', marginRight: '20px', marginTop: '20px' }}>
                    <ImportantReminderBanner onClose={handleCloseBanner} />
                </Box>
            )}

            <h3 className='panel-label' style={{ marginLeft: '20px' }}>Search Results ({searchResults.length})</h3>

            <RepositoryGrid
                compareIds={compareIds}
                searchResultsLoaded={searchResultsLoaded}
                searchResults={searchResults}
                handleAddToCompare={handleAddToCompare}
                handleRemoveFromCompare={handleRemoveFromCompare}
                queryParams={props.queryParams}
            />

        </Box>
    </div> );
}

const MainPage = () => {
    const [compareIds, setCompareIds] = useState([]);
    const [searchResults, setSearchResults] = useState([]);
    const [displayedResults, setDisplayedResults] = useState([]);
    const [filters, setFilters] = useState({});
    const [queryStr, setQueryStr] = useState("");
    const [searchResultsLoaded, setSearchResultsLoaded] = useState(false);
    const [searchStr, setSearchStr] = useState();
    const [showReminderBanner, setShowReminderBanner] = useState(true);

    const handleRemoveFromCompare = (repoId) => {
        setCompareIds(prevIds => prevIds.filter(id => id !== repoId));
    };

    // Filter search results by search string
    useEffect(() => {
        console.log('SEARCH: ', searchStr, searchResults);
        let filteredResults = searchResults;
        if (searchStr && searchResults.length > 0) {
            const lowerSearchTerm = searchStr.toLowerCase();
            filteredResults = searchResults.filter((item) => {
                const lowerNickname = item.nickname?.toLowerCase() || '';
                const lowerName = item.name?.toLowerCase() || '';
                const lowerDescription = item.description?.toLowerCase() || '';

                return lowerNickname.includes(lowerSearchTerm) ||
                       lowerName.includes(lowerSearchTerm) ||
                       lowerDescription.includes(lowerSearchTerm);
            });
        }
        setDisplayedResults(filteredResults);
    }, [searchStr, searchResults]);

    // Build query string whenever filters change
    useEffect(() => {
        setSearchResultsLoaded(false);
        let newQueryStr = "";

        if (Object.keys(filters).length > 0) {
            newQueryStr += "?";

            if (filters['names']) {
                newQueryStr += (newQueryStr === "?" ? "" : "&") + "names=[" + filters['names'] + "]";
            }

            if (filters['branches']) {
                newQueryStr += (newQueryStr === "?" ? "" : "&") + "affiliation=" + filters['branches'];
            }

            if (filters['fee']) {
                newQueryStr += (newQueryStr === "?" ? "" : "&") + "fee=" + filters['fee'];
            }

            if (filters['organisms']) {
                newQueryStr += (newQueryStr === "?" ? "" : "&") + "organisms=[" + filters['organisms'] + "]";
            }

            if (filters['metadata']) {
                newQueryStr += (newQueryStr === "?" ? "" : "&") + "metadata=" + filters['metadata'];
            }

            if (filters['data_types']) {
                newQueryStr += (newQueryStr === "?" ? "" : "&") + "data_types=[" + filters['data_types'] + "]";
            }

            if (filters['generalist']) {
                newQueryStr += (newQueryStr === "?" ? "" : "&") + "generalist=" + filters['generalist'];
            }

            if (filters['data_formats']) {
                newQueryStr += (newQueryStr === "?" ? "" : "&") + "data_formats=[" + filters['data_formats'] + "]";
            }

            if (filters['research_areas']) {
                newQueryStr += (newQueryStr === "?" ? "" : "&") + "research_areas=[" + filters['research_areas'] + "]";
            }

            if (filters['access_control']) {
                newQueryStr += (newQueryStr === "?" ? "" : "&") + "ac=" + filters['access_control'];
            }

            if (filters['individual']) {
                newQueryStr += (newQueryStr === "?" ? "" : "&") + "individual=" + filters['individual'];
            }

            if (filters['cert_required']) {
                newQueryStr += (newQueryStr === "?" ? "" : "&") + "cert_required=" + filters['cert_required'];
            }

            if (filters['volume_limit']) {
                newQueryStr += (newQueryStr === "?" ? "" : "&") + "volume_limit=" + filters['volume_limit'];
            }
        }

        console.log('query string:', newQueryStr);
        setQueryStr(newQueryStr);
    }, [filters]);

    // Fetch repositories when query string changes
    useEffect(() => {
        var requestUrl = process.env.REACT_APP_API_BASE_URL + "repositories";

        if (queryStr) {
            requestUrl += queryStr;
        }

        fetch(requestUrl)
            .then(response => response.json())
            .then((responseJson) => {
                setSearchResults(responseJson['repositories']);
                setSearchResultsLoaded(true);
            })
            .catch(error => {
                console.error('Error fetching repositories:', error);
                setSearchResultsLoaded(true);
            });
    }, [queryStr]);

    const handleAddToCompare = (event) => {
        var tempIds = [...compareIds];
        if ( event.target ) {
            if ( event.target.checked ) {
                if (compareIds.length === 3) {
                    // TODO Add popup for user
                    console.log("Already selected 3 repos");
                } else {
                    tempIds.push(event.target.id);
                }
            } else {
                tempIds = tempIds.filter(id => event.target.id !== id);
            }
            setCompareIds(tempIds);
        }
    };

    // Handle radio button filters
    const handleAddToFilters = (event) => {
        setFilters(prevFilters => {
            const newFilters = {...prevFilters};

            if (event.target.name === 'fee_group') {
                if (event.target.value === '') {
                    delete newFilters.fee;
                } else {
                    newFilters.fee = event.target.value;
                }
            }

            if (event.target.name === 'metadata_group') {
                if (event.target.value === '') {
                    delete newFilters.metadata;
                } else {
                    newFilters.metadata = event.target.value;
                }
            }

            if (event.target.name === 'ac_group') {
                if (event.target.value === '') {
                    delete newFilters.access_control;
                } else {
                    newFilters.access_control = event.target.value;
                }
            }

            if (event.target.name === 'individual_group') {
                if (event.target.value === '') {
                    delete newFilters.individual;
                } else {
                    newFilters.individual = event.target.value;
                }
            }

            if (event.target.name === "cert_group") {
                if (event.target.value === '') {
                    delete newFilters.cert_required;
                } else {
                    newFilters.cert_required = event.target.value;
                }
            }

            if (event.target.name === "data_volume_group") {
                if (event.target.value === '') {
                    delete newFilters.volume_limit;
                } else {
                    newFilters.volume_limit = event.target.value;
                }
            }

            return newFilters;
        });
    };

    const handleChangeNames = (event, value, reason) => {
        setFilters(prevFilters => {
            const newFilters = {...prevFilters};

            if (value.length > 0) {
                const names = value.map(v => "'" + v.name + "'");
                newFilters.names = names;
                console.log('Names filter:', newFilters.names);
            } else {
                delete newFilters.names;
            }

            return newFilters;
        });
    };

    const handleChangeBranches = (event, value, reason) => {
        setFilters(prevFilters => {
            const newFilters = {...prevFilters};

            if (value.length > 0) {
                const ids = value.map(v => v.id);
                newFilters.branches = ids;
            } else {
                delete newFilters.branches;
            }

            return newFilters;
        });
    };

    const handleChangeOrganisms = (event, value, reason) => {
        setFilters(prevFilters => {
            const newFilters = {...prevFilters};

            if (value.length > 0) {
                const ids = value.map(v => v.id);
                newFilters.organisms = ids;
            } else {
                delete newFilters.organisms;
            }

            return newFilters;
        });
    };

    const handleChangeResearchAreas = (event, value, reason) => {
        setFilters(prevFilters => {
            const newFilters = {...prevFilters};

            if (value.length > 0) {
                const ids = value.map(v => v.id);
                newFilters.research_areas = ids;
            } else {
                delete newFilters.research_areas;
            }

            return newFilters;
        });
    };

    const handleChangeDataTypes = (event, value, reason) => {
        setFilters(prevFilters => {
            const newFilters = {...prevFilters};

            if (value.length > 0) {
                const ids = value.map(v => v.id);
                newFilters.data_types = ids;
            } else {
                delete newFilters.data_types;
            }

            return newFilters;
        });
    };

    const handleChangeGeneralist = (event, value, reason) => {
        setFilters(prevFilters => {
            const newFilters = {...prevFilters};

            if (value.length === 0) {
                // Nothing selected - show all
                delete newFilters.generalist;
            } else if (value.length === 1) {
                // One option selected - filter by that boolean value
                newFilters.generalist = value[0].id; // "true" or "false"
            } else {
                // Both selected - show all (same as nothing selected)
                delete newFilters.generalist;
            }

            return newFilters;
        });
    };

    const handleChangeDataFormats = (event, value, reason) => {
        setFilters(prevFilters => {
            const newFilters = {...prevFilters};

            if (value.length > 0) {
                const ids = value.map(v => v.id);
                newFilters.data_formats = ids;
            } else {
                delete newFilters.data_formats;
            }

            return newFilters;
        });
    };

    const handleSearch = (event) => {
        setSearchStr(event.target.value);
    };

    const handleCloseBanner = () => {
        setShowReminderBanner(false);
    };

    return( <Container maxWidth='xl' style={{ height: '100%', padding: '0' }}>
        <main>
            <Grid container spacing={2}>

               <Grid xs={12}>
                    <ComparisonSelection
                        compareIds={compareIds}
                        handleSearch={handleSearch}
                        handleRemoveFromCompare={handleRemoveFromCompare}
                    />
                </Grid>

                <Grid xs={12} s={2} md={4}>
                    <SelectorInputs
                        filters={filters}
                        handleAddToFilters={handleAddToFilters}
                        handleChangeBranches={handleChangeBranches}
                        handleChangeOrganisms={handleChangeOrganisms}
                        handleChangeResearchAreas={handleChangeResearchAreas}
                        handleChangeDataTypes={handleChangeDataTypes}
                        handleChangeDataFormats={handleChangeDataFormats}
                        handleChangeNames={handleChangeNames}
                        handleChangeGeneralist={handleChangeGeneralist}
                    />
                </Grid>

                <Grid xs={12} s={10} md={8}>
                <RecommendedRepositories
                    compareIds={compareIds}
                    searchResultsLoaded={searchResultsLoaded}
                    searchResults={displayedResults}
                    handleAddToCompare={handleAddToCompare}
                    handleRemoveFromCompare={handleRemoveFromCompare}  // NEW LINE
                    showReminderBanner={showReminderBanner}
                    handleCloseBanner={handleCloseBanner}
                />
                </Grid>
            </Grid>
        </main>
    </Container> );
}

const App = () => {
    const { authString, authRole, setAuthString, clearAuthString } = useAuthString();
    let maintenanceBanner = process.env.REACT_APP_MAINTENANCE_BANNER;

    useEffect(() => {
        document.title = "Data Repository Finder";
        console.log('banner env: ' + process.env.REACT_APP_MAINTENANCE_BANNER);
    }, []);

    return ( <div className='app-background'>
        <PageNavigation authString={authString} clearAuthString={clearAuthString} />
        <Container maxWidth='xl' style={{height:'100%'}}>
            <MainHeader maintenanceBanner={maintenanceBanner} />

            <div className="content-wrapper">
                <Routes>
                    { process.env.REACT_APP_MAINTENANCE_MODE === 'true'
                        ?
                        <>
                            <Route path="/" index element={<MaintenancePage />} />
                            <Route path="/repository_list" element={<MaintenancePage />} />
                            <Route path="/login" element={<MaintenancePage />} />
                            <Route path="/data_definitions" element={<MaintenancePage />} />
                            <Route path="/repositories/management" element={<MaintenancePage />} />
                        </>
                        :
                        <>
                            <Route path="/" index element={<MainPage />} />
                            <Route path="/repository_list" element={<MainPage />} />
                            <Route path="/repositories/:id" element={<RepositoryHomePage />} />
                            <Route path="/repositories/compare" element={<RepositoryComparisonPage />} />
                            <Route path="/repositories/management" element={<RepositoryManagementHome authString={authString} setAuthString={setAuthString} />} />
                            <Route path="/repositories/management/add" element={<RepositoryAddEditPage authString={authString} authRole={authRole} setAuthString={setAuthString} editMode={false} />} />
                            <Route path="/repositories/management/edit/:id" element={<RepositoryAddEditPage authString={authString} authRole={authRole} setAuthString={setAuthString} editMode={true} />} />
                            <Route path="/repositories/management/preview/:id" element={<RepositoryPreviewPage authString={authString} authRole={authRole} setAuthString={setAuthString} />} />
                            <Route path="/repositories/management/accepted" element={<RepositoryAcceptedForReview authString={authString} setAuthString={setAuthString} />} />
                            <Route path="/repositories/management/accepted-publish" element={<RepositoryAcceptedForPublish authString={authString} authRole={authRole} setAuthString={setAuthString} />} />
                            <Route path="/data_definitions" element={<DataDictionaryPage />} />
                            <Route path="/logout" element={<Login authString={authString} setAuthString={setAuthString} clearAuthString={clearAuthString} isLoggedIn={true} />} />
                            <Route path="/login" element={<Login authString={authString} setAuthString={setAuthString} clearAuthString={clearAuthString} isLoggedIn={false} />} />
                        </>
                    }
                </Routes>
            </div>

            <Footer />
        </Container>
    </div> );
}


const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
    <BrowserRouter>
        <App />
    </BrowserRouter>
);